import { standardRevivers, standardReplacers } from './Json';
import ValidationResult from './ValidationResult';

export function handleFetchResponse(response: Response) {
    const { MiniProfiler } = window as any;
    const profilerResultsExists = document.querySelector('.profiler-results') !== null;

    if (MiniProfiler && profilerResultsExists) {
        try {
            const mpids = response.headers.get('X-MiniProfiler-Ids');
            if (mpids) {
                const idArray = JSON.parse(mpids) as string[];
                if (Array.isArray(idArray) && idArray.length > 0) {
                    MiniProfiler.fetchResults(JSON.parse(mpids) as string[]);
                }
            }
        } catch (err: any) {
            // eslint-disable-next-line no-console
            console.error('error on mini-profiler fetch', err);
        }
    }
}

export class SimpleRequestError extends Error {
    constructor(
        message: string,
        statusCode: number,
        statusMessage: string
    ) {
        super(message);
        this.statusCode = statusCode;
        this.statusMessage = statusMessage;
    }

    public statusCode: number;

    public statusMessage: string;
}

export const SimpleRequestAsync = async <T = any>(
    url: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    expectResponseBody = true,
    body: any = undefined,
    signal: AbortSignal | undefined = undefined,
    additionalHeaders: [string, string][] | undefined = undefined
) => {
    const headers: [string, string][] = [
        ['pragma', 'no-cache'],
        ['cache-control', 'no-cache']
    ];

    if (body !== undefined) {
        headers.push(['Content-Type', 'application/json']);
    }

    if (additionalHeaders !== undefined) {
        additionalHeaders.forEach(h => headers.push(h));
    }

    const response = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body, standardReplacers) : undefined,
        credentials: 'same-origin',
        signal
    });

    handleFetchResponse(response);

    if (response.status.toString().startsWith('5')) {
        let text = '';
        if (response.body !== null) {
            text = await response.text();
        }

        throw new SimpleRequestError(
            `API call failed to ${url}. Status code ${response.status}
            : ${response.statusText}. ${text}`,
            response.status,
            response.statusText
        );
    }

    if (response.status === 404) {
        return undefined;
    }

    if (response.status.toString().startsWith('4')) {
        let text = '';
        if (response.body !== null) {
            text = await response.text();
        }
        throw new SimpleRequestError(
            `${response.status}. ${text}`,
            response.status,
            response.statusText
        );
    }

    if (response.ok && expectResponseBody) {
        const text = await response.text();
        return JSON.parse(text, standardRevivers) as T;
    }

    return undefined;
};

export const ValidatedRequestAsync = async <T>(
    url: string,
    method: string,
    body: any,
    webApi = false,
    expectResponseBody = true,
    additionalHeaders: [string, string][] | undefined = undefined
) => {
    const headers: [string, string][] = [
        ['Content-Type', 'application/json'],
        ['pragma', 'no-cache'],
        ['cache-control', 'no-cache']
    ];

    if (additionalHeaders !== undefined) {
        additionalHeaders.forEach(h => headers.push(h));
    }

    const response = await fetch(url, {
        method,
        credentials: 'same-origin',
        headers,
        body: JSON.stringify(body, standardReplacers)
    });

    handleFetchResponse(response);

    if (webApi) {
        return await ValidationResult.fromWebApiAsync<T>(response, expectResponseBody);
    }

    return await ValidationResult.fromMvcAsync<T>(response, expectResponseBody);
};

export const ValidatedFormRequestAsync = async <T>(
    url: string,
    method: string,
    body: FormData,
    webApi = false,
    expectResponseBody = true,
    additionalHeaders: [string, string][] | undefined = undefined
) => {
    // DO NOT SET Content-Type
    // eslint-disable-next-line max-len
    // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest_API/Using_FormData_Objects#:~:text=the%20open()%20call.-,Warning,-%3A%20When%20using
    const headers: [string, string][] = [
        ['pragma', 'no-cache'],
        ['cache-control', 'no-cache']
    ];

    if (additionalHeaders !== undefined) {
        additionalHeaders.forEach(h => headers.push(h));
    }

    const response = await fetch(url, {
        method,
        credentials: 'same-origin',
        headers,
        body
    });

    handleFetchResponse(response);

    if (webApi) {
        return await ValidationResult.fromWebApiAsync<T>(response, expectResponseBody);
    }

    return await ValidationResult.fromMvcAsync<T>(response, expectResponseBody);
};
